import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Chip,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Tooltip from "@material-ui/core/Tooltip";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import HomeIcon from "@material-ui/icons/Home";
import { useHistory } from "react-router-dom";
import deepPurple from "@material-ui/core/colors/deepPurple";
import Table from "../shared/Table";
import { useModals } from "../../hooks/useModals";
import { useAppContext } from "../../api/AppContext";
import { useSnackbar } from "notistack";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import LinkIcon from "@material-ui/icons/Link";
import {
  COMPLETION_STATUS_STYLES,
  getLanguageChipStyles,
  getLocalizedField,
  getNewsFieldByLanguage,
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  hasNewsContentInLanguage,
  isNewsLanguageCompleteForSave,
  LANGUAGE_CHIP_LABELS,
} from "../shared/languageConfig";

const NewsList = () => {
  const [isCopyNewsLoading, setIsCopyNewsLoading] = useState(false);
  const [localNews, setLocalNews] = useState([]);
  const [selectedCids, setSelectedCids] = useState([]);
  const [selectedJourneys, setSelectedJourneys] = useState([]);
  const [isBulkAssociating, setIsBulkAssociating] = useState(false);
  const modals = useModals();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const {
    copilotNews,
    isCopilotNewsLoading,
    fetchCopilotNews,
    createCopilotNews,
    updateCopilotNews,
    configurations,
    fetchConfigurations,
    isConfigurationsLoading,
    journeys,
    fetchJourneys,
    user,
  } = useAppContext();

  const validLanguages = useMemo(() => getValidLanguagesFromUser(user), [user]);
  const [activeTableLanguage, setActiveTableLanguage] = useState(() =>
    getPreferredLanguageFromUser(user, validLanguages)
  );

  useEffect(() => {
    const preferred = getPreferredLanguageFromUser(user, validLanguages);
    setActiveTableLanguage((current) =>
      validLanguages.includes(current) ? current : preferred
    );
  }, [user, validLanguages]);

  useEffect(() => {
    fetchCopilotNews();
    fetchConfigurations();
    fetchJourneys();
  }, [fetchCopilotNews, fetchConfigurations, fetchJourneys]);

  useEffect(() => {
    setLocalNews(copilotNews);
  }, [copilotNews]);

  const filteredNews = useMemo(() => {
    let next = localNews || [];

    if (
      (selectedCids && selectedCids.length > 0) ||
      (selectedJourneys && selectedJourneys.length > 0)
    ) {
      const cidSet = new Set(selectedCids || []);
      const journeySet = new Set(selectedJourneys || []);

      next = next.filter((n) => {
        const newsCids = Array.isArray(n.conditions?.cid)
          ? n.conditions.cid
          : [];
        const newsJourneys = Array.isArray(n.conditions?.journey)
          ? n.conditions.journey
          : [];

        const cidMatch =
          cidSet.size === 0 || newsCids.some((c) => cidSet.has(c));
        const journeyMatch =
          journeySet.size === 0 || newsJourneys.some((j) => journeySet.has(j));
        return cidMatch && journeyMatch;
      });
    }

    return next.filter((news) =>
      hasNewsContentInLanguage(news, activeTableLanguage)
    );
  }, [activeTableLanguage, localNews, selectedCids, selectedJourneys]);

  const cidOptions = useMemo(() => {
    return (configurations || [])
      .map((c) => ({ id: c.cid, label: c.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [configurations]);

  const cidById = useMemo(() => {
    return new Map(cidOptions.map((o) => [o.id, o]));
  }, [cidOptions]);

  const journeyOptions = useMemo(() => {
    return (journeys || [])
      .map((j) => ({ id: j.id, label: j.internalName || j.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [journeys]);

  const journeyById = useMemo(() => {
    return new Map(journeyOptions.map((o) => [o.id, o]));
  }, [journeyOptions]);

  const newsOptions = useMemo(() => {
    return (localNews || [])
      .map((n) => ({
        id: n.id,
        label: n.text_default?.title || n.text_en?.title || "Untitled news",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [localNews]);

  const openAssociateDialog = useCallback(() => {
    modals.openModal(modals.modalTypes.AssociateNews, {
      cidOptions,
      initialCids: selectedCids,
      initialJourneys: selectedJourneys,
      journeyOptions,
      newsOptions,
      onConfirm: async ({ cids, journeys, newsIds } = {}) => {
        if (!newsIds || newsIds.length === 0) {
          enqueueSnackbar("Select at least one news item to associate.", {
            variant: "warning",
          });
          return;
        }
        if (
          (!cids || cids.length === 0) &&
          (!journeys || journeys.length === 0)
        ) {
          enqueueSnackbar("Select at least one CID or journey to apply.", {
            variant: "warning",
          });
          return;
        }
        const newsById = new Map((localNews || []).map((n) => [n.id, n]));
        setIsBulkAssociating(true);
        try {
          const results = await Promise.allSettled(
            newsIds.map(async (id) => {
              const news = newsById.get(id);
              if (!news) {
                throw new Error(`News ${id} not found`);
              }
              const prev = news.conditions || {};
              const oldCid = Array.isArray(prev.cid) ? prev.cid : [];
              const oldJourney = Array.isArray(prev.journey)
                ? prev.journey
                : [];
              const nextCid = [...new Set([...oldCid, ...cids])];
              const nextJourney = [...new Set([...oldJourney, ...journeys])];
              await updateCopilotNews({
                conditions: {
                  ...prev,
                  cid: nextCid,
                  journey: nextJourney,
                },
                id: news.id,
              });
            })
          );
          const failed = results.filter((r) => r.status === "rejected").length;
          const ok = results.length - failed;
          if (failed === 0) {
            enqueueSnackbar(
              `Updated ${ok} news item${ok === 1 ? "" : "s"} successfully.`,
              { variant: "success" }
            );
          } else {
            enqueueSnackbar(
              `Updated ${ok} of ${results.length} news items. ${failed} failed.`,
              { variant: failed === results.length ? "error" : "warning" }
            );
          }
          await fetchCopilotNews();
        } catch (e) {
          enqueueSnackbar("Bulk associate failed.", { variant: "error" });
          throw e;
        } finally {
          setIsBulkAssociating(false);
        }
      },
    });
  }, [
    cidOptions,
    enqueueSnackbar,
    fetchCopilotNews,
    journeyOptions,
    localNews,
    modals,
    newsOptions,
    selectedCids,
    selectedJourneys,
    updateCopilotNews,
  ]);

  const handleCreate = useCallback(() => {
    history.push(`/news/news/new/start`, {
      isNew: true,
    });
  }, [history]);

  const handleEdit = useCallback(
    ({ id }) => {
      history.push(`/news/news/${id}`);
    },
    [history]
  );

  const handleDelete = useCallback(
    (news) => {
      modals.openConfirmation({
        onConfirm: async () => {
          try {
            await updateCopilotNews({
              id: news.id,
              isdeleted: true,
            });
            enqueueSnackbar("News deleted successfully!", {
              variant: "success",
            });
          } catch (error) {
            enqueueSnackbar("Error deleting news", { variant: "error" });
          }
        },
        text: `You want to delete news "${news.text_default.title}"`,
      });
    },
    [modals, updateCopilotNews, enqueueSnackbar]
  );

  const handlePublish = useCallback(
    async (news) => {
      try {
        await updateCopilotNews({
          id: news.id,
          ispublished: !news.ispublished,
        });
        enqueueSnackbar(
          news.ispublished
            ? "News unpublished successfully!"
            : "News published successfully!",
          { variant: "success" }
        );
      } catch (error) {
        enqueueSnackbar("Error updating news status", { variant: "error" });
      }
    },
    [updateCopilotNews, enqueueSnackbar]
  );

  const handleHomeDisplay = useCallback(
    async (news) => {
      const nextValue = !news.conditions?.homeDisplay;
      try {
        await updateCopilotNews({
          id: news.id,
          conditions: {
            ...(news.conditions || {}),
            homeDisplay: nextValue,
          },
        });
        enqueueSnackbar(
          nextValue
            ? "News added to home display!"
            : "News removed from home display!",
          { variant: "success" }
        );
      } catch (error) {
        enqueueSnackbar("Error updating home display status", {
          variant: "error",
        });
      }
    },
    [updateCopilotNews, enqueueSnackbar]
  );

  const buildNewsObject = useCallback(({ news }) => {
    let content = [];
    if (news.content && Array.isArray(news.content)) {
      content = news.content.map((item) => {
        return {
          data: {
            en: item.data?.en || "",
            es: item.data?.es || "",
            fr: item.data?.fr || "",
            it: item.data?.it || "",
          },
          type: item.type || "",
        };
      });
    }
    return {
      conditions: {
        allowSubscription: news.conditions?.allowSubscription || false,
        assignedField: news.conditions?.assignedField || "",
        cid: news.conditions?.cid,
        journey: news.conditions?.journey,
        language: news.conditions?.language,
      },
      content: content,
      expirationdate: news.expirationdate,
      image: news.image,
      isNew: true,
      startdate: news.startdate,
      text_default: news.text?.it || news.text_default || {},
      text_en: news.text?.en || news.text_en || {},
      text_es: news.text?.es || news.text_es || {},
      text_fr: news.text?.fr || news.text_fr || {},
    };
  }, []);

  const handleCopy = useCallback(
    (news) => {
      modals.openConfirmation({
        onConfirm: async () => {
          try {
            const newNews = buildNewsObject({ news });
            await createCopilotNews(newNews);
            enqueueSnackbar("News copied successfully!", {
              variant: "success",
            });
          } catch (error) {
            enqueueSnackbar("Error copying news", { variant: "error" });
          } finally {
            setIsCopyNewsLoading(false);
          }
        },
        text: `You want to copy news "${news.text_default?.title}"`,
      });
    },
    [modals, buildNewsObject, createCopilotNews, enqueueSnackbar]
  );

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const columns = useMemo(
    () => [
      {
        title: "Title",
        field: "title",
        width: "50%",
        customSort: (a, b) => {
          const fieldName = getNewsFieldByLanguage(activeTableLanguage);
          const aTitle = (a?.[fieldName]?.title || "").toLowerCase();
          const bTitle = (b?.[fieldName]?.title || "").toLowerCase();
          return aTitle.localeCompare(bTitle);
        },
        customFilterAndSearch: (term, rowData) => {
          const fieldName = getNewsFieldByLanguage(activeTableLanguage);
          const title = rowData?.[fieldName]?.title || "";
          return title.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => {
          const fieldName = getNewsFieldByLanguage(activeTableLanguage);
          return (
            <Box>
              <Typography variant="body1">
                {rowData?.[fieldName]?.title || "No title"}
              </Typography>
            </Box>
          );
        },
      },
      {
        title: "Start Date",
        field: "startdate",
        width: "15%",
        customSort: (a, b) => {
          const aDate = new Date(a.startdate || 0);
          const bDate = new Date(b.startdate || 0);
          return aDate - bDate;
        },
        customFilterAndSearch: (term, rowData) => {
          const dateStr = formatDate(rowData.startdate);
          return dateStr.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => (
          <Typography variant="body2">
            {formatDate(rowData.startdate)}
          </Typography>
        ),
      },
      {
        title: "Published",
        field: "ispublished",
        width: "10%",
        customSort: (a, b) => {
          return (a.ispublished ? 1 : 0) - (b.ispublished ? 1 : 0);
        },
        customFilterAndSearch: (term, rowData) => {
          const status = rowData.ispublished ? "published" : "unpublished";
          return status.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => (
          <Box alignItems="center" display="flex">
            <Tooltip title={rowData.ispublished ? "Unpublish" : "Publish"}>
              <IconButton
                aria-label="toggle publish"
                size="small"
                onClick={() => handlePublish(rowData)}
              >
                {rowData.ispublished ? (
                  <VisibilityIcon style={{ color: "green" }} />
                ) : (
                  <VisibilityOffIcon style={{ color: "red" }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        title: "Tags",
        field: "tags",
        width: "20%",
        customFilterAndSearch: (term, rowData) => {
          const tags = (rowData.tags || [])
            .map((t) => getLocalizedField(t.tagname, activeTableLanguage))
            .filter(Boolean)
            .join(", ");
          return tags.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => (
          <Typography variant="body2">
            {(rowData.tags || [])
              .map((t) => getLocalizedField(t.tagname, activeTableLanguage))
              .filter(Boolean)
              .join(", ") || "-"}
          </Typography>
        ),
      },
      {
        title: "Home Display",
        field: "homeDisplay",
        width: "15%",
        customSort: (a, b) => {
          const aDisplay = a.conditions?.homeDisplay ? 1 : 0;
          const bDisplay = b.conditions?.homeDisplay ? 1 : 0;
          return aDisplay - bDisplay;
        },
        customFilterAndSearch: (term, rowData) => {
          const displayStatus = rowData.conditions?.homeDisplay
            ? "true"
            : "false";
          return displayStatus.toLowerCase().includes(term.toLowerCase());
        },
        render: (rowData) => (
          <Box alignItems="center" display="flex">
            <Tooltip
              title={
                rowData.conditions?.homeDisplay
                  ? "Remove from home display"
                  : "Add to home display"
              }
            >
              <IconButton
                aria-label="toggle home display"
                size="small"
                onClick={() => handleHomeDisplay(rowData)}
              >
                <HomeIcon
                  style={{
                    color: rowData.conditions?.homeDisplay ? "green" : "red",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [activeTableLanguage, handleHomeDisplay]
  );

  const languageChips = useMemo(
    () => (
      <Box display="flex" flexWrap="wrap" pt={0.5}>
        {validLanguages.map((language) => (
          <Button
            key={language}
            size="small"
            style={{
              ...getLanguageChipStyles(activeTableLanguage === language),
              borderRadius: 20,
              boxShadow: "none",
              marginBottom: 8,
              marginRight: 8,
              minWidth: 90,
            }}
            variant="contained"
            onClick={() => setActiveTableLanguage(language)}
          >
            {LANGUAGE_CHIP_LABELS[language] || language.toUpperCase()}
          </Button>
        ))}
      </Box>
    ),
    [activeTableLanguage, validLanguages]
  );

  const actions = useMemo(
    () => [
      {
        icon: (props) => (
          <FileCopyIcon {...props} style={{ color: "#0000008a" }} />
        ),
        onClick: (event, rowData) => handleCopy(rowData),
        tooltip: "Copy",
      },
      {
        icon: (props) => <EditIcon {...props} style={{ color: "#0000008a" }} />,
        onClick: (event, rowData) => handleEdit(rowData),
        tooltip: "Edit",
      },
      {
        icon: (props) => (
          <DeleteIcon {...props} style={{ color: "#0000008a" }} />
        ),
        onClick: (event, rowData) => handleDelete(rowData),
        tooltip: "Delete",
      },
    ],
    [handleCopy, handleEdit, handleDelete]
  );

  const tableActions = useMemo(
    () => [
      <Button
        key="add"
        color="primary"
        endIcon={<AddIcon />}
        variant="contained"
        onClick={handleCreate}
      >
        Add news
      </Button>,
    ],
    [handleCreate]
  );

  const options = useMemo(
    () => ({
      pageSize: 10,
      pageSizeOptions: [5, 10, 15],
      rowStyle: (rowData) => {
        const isPartial =
          hasNewsContentInLanguage(rowData, activeTableLanguage) &&
          !isNewsLanguageCompleteForSave(rowData, activeTableLanguage);

        if (isPartial) {
          return {
            backgroundColor: COMPLETION_STATUS_STYLES.partial.backgroundColor,
          };
        }

        if (!rowData.ispublished) {
          return {
            backgroundColor: deepPurple[50],
          };
        }

        return {};
      },
      search: true,
      filtering: false,
    }),
    [activeTableLanguage]
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="flex-end"
            style={{ flexWrap: "wrap", gap: 8 }}
          >
            {(user?.groups || []).includes("Admins") && (
              <>
                <Button
                  color="primary"
                  endIcon={<LinkIcon />}
                  variant="outlined"
                  onClick={openAssociateDialog}
                >
                  Associate multiple news
                </Button>
                <Button
                  color="primary"
                  disabled={isConfigurationsLoading}
                  variant="outlined"
                  onClick={() => {
                    const copilotCidOptions = (configurations || []).filter(
                      (c) =>
                        (c.isCopilotGo ?? false) === true &&
                        !String(c.cid).toLowerCase().startsWith("lgo")
                    );
                    const copilotCids = copilotCidOptions.map((c) => c.cid);
                    const journeysForCidsSet = new Set(
                      copilotCidOptions.flatMap((c) =>
                        Array.isArray(c.availableJourneys)
                          ? c.availableJourneys
                          : []
                      )
                    );
                    setSelectedCids(copilotCids);
                    setSelectedJourneys(Array.from(journeysForCidsSet));
                  }}
                >
                  CopilotGo News
                </Button>
              </>
            )}

            <Autocomplete
              disableCloseOnSelect
              multiple
              getOptionLabel={(option) => option.label}
              options={cidOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  label={`CID${
                    selectedCids?.length
                      ? ` (${selectedCids.length} selected)`
                      : ""
                  }`}
                  placeholder="Search CID..."
                  size="small"
                  variant="outlined"
                />
              )}
              renderTags={(tagValue, getTagProps) => {
                const maxVisible = 2;
                const visible = (tagValue || []).slice(0, maxVisible);
                const hiddenCount = (tagValue || []).length - visible.length;
                return (
                  <Box
                    style={{
                      alignItems: "center",
                      display: "flex",
                      flexWrap: "nowrap",
                    }}
                  >
                    {visible.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.label}
                        size="small"
                        style={{ marginRight: 4 }}
                      />
                    ))}
                    {hiddenCount > 0 && (
                      <Chip label={`+${hiddenCount}`} size="small" />
                    )}
                  </Box>
                );
              }}
              style={{ minWidth: 260, height: 40 }}
              value={(selectedCids || [])
                .map((id) => cidById.get(id))
                .filter(Boolean)}
              onChange={(e, value) =>
                setSelectedCids((value || []).map((v) => v.id))
              }
            />

            <Autocomplete
              disableCloseOnSelect
              multiple
              getOptionLabel={(option) => option.label}
              options={journeyOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  label={`Journey${
                    selectedJourneys?.length
                      ? ` (${selectedJourneys.length} selected)`
                      : ""
                  }`}
                  placeholder="Search journey..."
                  size="small"
                  variant="outlined"
                />
              )}
              renderTags={(tagValue, getTagProps) => {
                const maxVisible = 2;
                const visible = (tagValue || []).slice(0, maxVisible);
                const hiddenCount = (tagValue || []).length - visible.length;
                return (
                  <Box
                    style={{
                      alignItems: "center",
                      display: "flex",
                      flexWrap: "nowrap",
                    }}
                  >
                    {visible.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.label}
                        size="small"
                        style={{ marginRight: 4 }}
                      />
                    ))}
                    {hiddenCount > 0 && (
                      <Chip label={`+${hiddenCount}`} size="small" />
                    )}
                  </Box>
                );
              }}
              style={{ minWidth: 300, height: 40 }}
              value={(selectedJourneys || [])
                .map((id) => journeyById.get(id))
                .filter(Boolean)}
              onChange={(e, value) =>
                setSelectedJourneys((value || []).map((v) => v.id))
              }
            />
          </Box>
        </Grid>
        <Grid item>
          <Table
            actions={actions}
            columns={columns}
            data={filteredNews}
            isLoading={
              isCopilotNewsLoading || isCopyNewsLoading || isBulkAssociating
            }
            languageChips={languageChips}
            options={options}
            refetch={fetchCopilotNews}
            tableActions={tableActions}
            title="News"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewsList;
